import type { CorpusItemDetail, OptionCorpus } from '@silex/shared';

import { FigureSvg } from './FigureSvg';

/**
 * CE QUE L'ÉLÈVE REÇOIT — la colonne de gauche du panneau de révision.
 *
 * Un relecteur qui corrige sans l'exercice sous les yeux corrige un
 * formulaire, pas un exercice : la consigne se relit dans un champ de deux
 * lignes, les options en file d'entrées de saisie, et la figure — quand il y
 * en a une — ne se voyait qu'en descendant sous les champs. Sur 687 items à
 * relire, c'est la différence entre juger un exercice et juger un formulaire.
 *
 * L'aperçu suit la SAISIE : ce qu'on vient d'écrire s'y affiche tel qu'il
 * sera servi.
 *
 * Le TEXTE SUPPORT y figure depuis Φ13 : `CorpusItemDetail` le transporte. Il
 * s'affiche avant l'énoncé, dans l'ordre où l'élève le lit.
 */
export function ApercuExercice({
  titre,
  enonce,
  consigne,
  options,
  figure,
  support,
}: {
  readonly titre: string;
  readonly enonce: string;
  readonly consigne: string;
  readonly options: readonly OptionCorpus[];
  /** La figure stockée, ou `null` — un item sur sept en porte une. */
  readonly figure: Record<string, unknown> | null;
  /** Le texte sur lequel porte la question, ou `null`. */
  readonly support: CorpusItemDetail['support'];
}) {
  return (
    <section className="card" aria-label="L’exercice tel que l’élève le reçoit">
      <span className="stage-label">Ce que l’élève reçoit</span>
      <h3>{titre}</h3>

      {support ? <TexteSupport support={support} /> : null}

      <p className="card-sub">{enonce}</p>
      {consigne.trim() !== '' ? <p className="card-sub">{consigne}</p> : null}

      {figure ? (
        <FigureSvg spec={figure as never} etiquetteSurface="La figure de l’énoncé" />
      ) : null}

      {options.length > 0 ? (
        <ul>
          {options.map((o) => (
            <li key={o.id}>
              <span className="badge">{o.id}</span> {o.texte}{' '}
              {/* La bonne réponse est celle qui ne pointe aucune erreur type —
                  la même règle que celle dont le validateur se sert. */}
              {o.erreurType === undefined ? (
                <span className="badge badge--acquis">Bonne réponse</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/**
 * LE TEXTE SUPPORT — lu, jamais saisi.
 *
 * `CorpusEditionRequest` ne porte aucun champ `support` : la console ne peut
 * pas l'enregistrer. Un champ modifiable dont le contenu ne partirait nulle
 * part est exactement le défaut corrigé sur la portée « fond » ; celui-ci a
 * donc la forme d'un texte, pas celle d'un formulaire.
 *
 * Les numéros sont ceux que l'élève a sous les yeux, et les questions y
 * renvoient — « entre les lignes 35 et 40 ». Les renuméroter à partir de 1
 * les ferait mentir.
 */
function TexteSupport({ support }: { support: NonNullable<CorpusItemDetail['support']> }) {
  return (
    <section aria-label="Le texte support">
      <span className="stage-label">Texte support</span>
      <h4>{support.titre}</h4>
      {support.genre ? <p className="note-line">{support.genre}</p> : null}
      {/* Le texte défile dans son propre cadre : soixante lignes pousseraient
          sinon les champs de correction hors de l'écran. */}
      <div style={{ maxHeight: 320, overflowY: 'auto', marginTop: 8 }}>
        {support.lignes.map((ligne) => (
          <div key={ligne.n} style={{ display: 'grid', gridTemplateColumns: '2.5rem 1fr', gap: 8 }}>
            <span
              style={{
                fontFamily: 'var(--f-data)',
                fontSize: 12,
                color: 'var(--sec)',
                textAlign: 'right',
              }}
            >
              {ligne.n}
            </span>
            <span style={{ fontSize: 14, lineHeight: 1.6 }}>{ligne.texte}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
